import type { Promotion } from '@/domain';
import { cn } from '@/lib/cn';
import { EmptyState, Spinner, Toast } from '@/ui/common';
import { PromoCard } from './PromoCard';

const FILTER_CHIPS = [
  { id: 'all', labelKey: 'All', fallback: 'All', selected: true },
  { id: 'sports', labelKey: 'Sports', fallback: 'Sports', selected: false },
  { id: 'casino', labelKey: 'Casino', fallback: 'Casino', selected: false },
  { id: 'welcome', labelKey: 'Welcome', fallback: 'Welcome', selected: false },
] as const;

export interface PromotionsPanelProps {
  promotions: Promotion[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  label: (key: string, fallback: string) => string;
  className?: string;
}

/**
 * Promotions list body — Figma Desktop/Mobile Promo List.
 * Filter chips are visual-only (CMS has no category); “All” stays selected.
 */
export function PromotionsPanel({
  promotions,
  isLoading,
  isError,
  errorMessage,
  label,
  className,
}: PromotionsPanelProps) {
  return (
    <div className={cn('flex w-full flex-col gap-4 lg:gap-6', className)}>
      <header className="flex flex-col gap-4 lg:gap-6">
        <h1 className="text-[20px] leading-7 font-bold tracking-[-0.1px] text-text-primary lg:text-[32px] lg:leading-10 lg:tracking-[-0.48px]">
          {label('Promotions', 'Promotions')}
        </h1>

        <div
          className="flex gap-2 overflow-x-auto"
          role="group"
          aria-label={label('Promotion filters', 'Promotion filters')}
        >
          {FILTER_CHIPS.map((chip) => (
            <span
              key={chip.id}
              aria-current={chip.selected ? 'true' : undefined}
              className={cn(
                'inline-flex shrink-0 items-center rounded-full px-4 py-2 text-[12px] leading-4 tracking-[0.15px]',
                chip.selected
                  ? 'bg-odds-selected font-medium text-odds-selected-text'
                  : 'border border-border-default bg-bg-surface font-medium text-odds-default-text',
              )}
            >
              {label(chip.labelKey, chip.fallback)}
            </span>
          ))}
        </div>
      </header>

      {isError ? (
        <Toast tone="danger">
          {label('Could not load promotions', 'Could not load promotions')}
          {errorMessage ? `: ${errorMessage}` : ''}
        </Toast>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner label={label('Loading promotions', 'Loading promotions')} />
        </div>
      ) : promotions.length === 0 && !isError ? (
        <EmptyState
          icon="promotions"
          title={label('No promotions', 'No promotions')}
          description={label(
            'Check back soon for new offers.',
            'Check back soon for new offers.',
          )}
        />
      ) : (
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:gap-4">
          {promotions.map((promo) => (
            <PromoCard
              key={String(promo.id)}
              promotion={promo}
              viewOfferLabel={label('ViewOffer', 'View offer')}
              className="lg:w-[calc((100%-2rem)/3)] lg:max-w-none xl:w-[400px] xl:max-w-[400px]"
            />
          ))}
        </div>
      )}
    </div>
  );
}
